import React, { Component, ErrorInfo, ReactNode } from 'react';
import styles from './ErrorBoundary.module.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to log this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: logErrorToService(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default spy-themed error UI
      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorContent}>
            <div className={styles.errorHeader}>
              <div className={styles.errorIcon}>⚠️</div>
              <h1 className={styles.errorTitle}>MISSION COMPROMISED</h1>
              <div className={styles.errorSubtitle}>
                System Malfunction Detected
              </div>
            </div>

            <div className={styles.errorMessage}>
              <p className={styles.errorDescription}>
                Agent, we've encountered an unexpected system error. 
                Your mission data is secure, but we need to reinitialize the interface.
              </p>
              
              <div className={styles.errorDetails}>
                <details className={styles.technicalDetails}>
                  <summary className={styles.detailsSummary}>
                    📋 Technical Report (For Debugging)
                  </summary>
                  <div className={styles.errorCode}>
                    <div className={styles.errorSection}>
                      <h4>Error:</h4>
                      <pre className={styles.errorText}>
                        {this.state.error?.toString()}
                      </pre>
                    </div>
                    {this.state.errorInfo && (
                      <div className={styles.errorSection}>
                        <h4>Component Stack:</h4>
                        <pre className={styles.errorText}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            </div>

            <div className={styles.errorActions}>
              <button 
                className={styles.retryButton}
                onClick={this.handleRetry}
              >
                🔄 Retry Mission
              </button>
              <button 
                className={styles.reloadButton}
                onClick={this.handleReload}
              >
                🔧 Full System Reset
              </button>
            </div>

            <div className={styles.errorFooter}>
              <p className={styles.errorHint}>
                💡 If this error persists, try refreshing the page or clearing your browser cache.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;